import React, { createContext, useContext, useState, useEffect, type ReactNode, useCallback } from 'react';
import { fetchAccountData, supabase } from '../lib/supabase';
import type { Session, User } from '@supabase/supabase-js';
import type { Account } from '../utils/types';
import * as Sentry from "@sentry/react";

interface AuthContextType {
    session: Session | null;
    user: User | null;
    account: Account | null;
    isLoading: boolean;
    signOut: () => Promise<void>;
    containsFeatureFlag: (featureKey: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [account, setAccount] = useState<Account | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAccount = async (id: string, signal: AbortSignal) => {
            try {
                const account = await fetchAccountData(id, signal);
                setAccount(account);
            } catch (error) {
                Sentry.captureException(error);
            }
        }

        const abortController = new AbortController();
        const signal = abortController.signal;

        setIsLoading(true);
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setUser(session?.user ?? null);
            fetchAccount(session?.user?.id || "", signal);
            setIsLoading(false);
        });

        const { data: authListener } = supabase.auth.onAuthStateChange(
            (_event, session) => {
                setSession(session);
                setUser(session?.user ?? null);
                setIsLoading(false);
            }
        );

        return () => {
            authListener?.subscription.unsubscribe();
            abortController.abort();
            setIsLoading(false);
        };
    }, []);

    const signOut = async () => {
        await supabase.auth.signOut({ scope: 'local' });
        window.location.href = '/login';
    };

    // Utility function to check if the account has a specific feature flag enabled.
    const containsFeatureFlag = useCallback(
        (featureKey: string) => {
            if (typeof account === "object" && account !== null && "feature_flags" in account) {
                const flags = account.feature_flags;
                return Array.isArray(flags) && flags.includes(featureKey);
            }
            return false;
        },
        [account],
    );

    const value = {
        session,
        user,
        account,
        isLoading,
        signOut,
        containsFeatureFlag,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};